import React, { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';

interface ClientPortalContextType {
  clientCpf: string | null;
  supplierSlug: string | null;
  setClientSession: (cpf: string, slug: string) => void;
  clearSession: () => void;
  isAuthenticated: boolean;
}

const ClientPortalContext = createContext<ClientPortalContextType | undefined>(undefined);

const SESSION_KEY = 'client_portal_session';
const SESSION_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes

interface SessionData {
  cpf: string;
  slug: string;
  timestamp: number;
}

export const ClientPortalProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [clientCpf, setClientCpf] = useState<string | null>(null);
  const [supplierSlug, setSupplierSlug] = useState<string | null>(null);

  // Load session from sessionStorage on mount
  useEffect(() => {
    try {
      const stored = sessionStorage.getItem(SESSION_KEY);
      if (stored) {
        const session: SessionData = JSON.parse(stored);
        const now = Date.now();
        
        // Check if session has expired
        if (now - session.timestamp < SESSION_TIMEOUT_MS) {
          setClientCpf(session.cpf);
          setSupplierSlug(session.slug);
        } else {
          // Session expired, clear it
          sessionStorage.removeItem(SESSION_KEY);
        }
      }
    } catch (error) {
      console.error('Error loading client portal session:', error);
      sessionStorage.removeItem(SESSION_KEY);
    }
  }, []);

  const setClientSession = useCallback((cpf: string, slug: string) => {
    const session: SessionData = {
      cpf,
      slug,
      timestamp: Date.now(),
    };
    
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(session));
    setClientCpf(cpf);
    setSupplierSlug(slug);
  }, []);

  const clearSession = useCallback(() => {
    sessionStorage.removeItem(SESSION_KEY);
    setClientCpf(null);
    setSupplierSlug(null);
  }, []);

  const isAuthenticated = Boolean(clientCpf && supplierSlug);

  return (
    <ClientPortalContext.Provider
      value={{
        clientCpf,
        supplierSlug,
        setClientSession,
        clearSession,
        isAuthenticated,
      }}
    >
      {children}
    </ClientPortalContext.Provider>
  );
};

export const useClientPortal = (): ClientPortalContextType => {
  const context = useContext(ClientPortalContext);
  if (context === undefined) {
    throw new Error('useClientPortal must be used within a ClientPortalProvider');
  }
  return context;
};
